import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { loadArtworkTexture, coverScale } from '../artwork-texture.js';
import { getMatrixAtlas } from '../matrix-atlas.js';
import { FLOAT_TEXTURES_OK } from '../float-textures.js';

// Shared-rig contract: forbidden. A screen of phosphor has no stage light.
const RIG = 'forbidden';
export { RIG };

// Orchestrator registry entry (see visualizer-registry.js).
export const PROFILE = {
  id: 'matrix',
  family: 'graphic',
  danceability: 0.75,
  energyLevel: 0.70,
  bpmMin: 100,
  bpmMax: 175,
  movement: 0.70,
  tonality: 0.45,
  prefers: { gridStrength: [0.55, 1.0] },
  requiresTier: 0,
  enabled: true
};

// Matrix: the screensaver's rain of code, turned: every glyph is also a
// pixel of the cover. From afar the picture; up close, columns of falling
// characters. Electro, techno, drum'n'bass, IDM, synthwave.
//
// THE WHOLE STATE IS ONE SMALL TEXTURE. A grid of 16 px cells (20, 24 at
// the lower tiers) - about 120 x 68 at 1080p - lives in a ping-pong pair of
// render targets the size of the grid: per cell the current glyph, the
// trail's brightness, a remanence, an age. One GPU pass a frame advances
// it; one full-screen quad draws it, reading the glyph from a 64-character
// SDF atlas (tools/build-matrix-atlas.mjs: Latin capitals, digits, mirrored
// half-width katakana, as in the reference) so the glyphs stay crisp at any
// cell size. The cells themselves are driven by DROPS: one or two per
// column, a head that falls at its own speed (the screen in two to eight
// beats) and a trail that dies behind it, born again at the top after a
// random wait once it has left by the bottom. The drops are a hundred-odd
// numbers kept on the CPU and uploaded as a tiny texture - the pass needs
// to know where each head was and is to light the cells it crossed.
//
// THE COVER IS THE LIGHT. A cell's brightness is trail(age) times
// mix(1, luminance(cover under the cell), uImage): with uImage at 1 the
// rain DRAWS the cover, and a dark region of the picture stays dark however
// many drops cross it. In IMAGE mode a glyph takes the cover's colour under
// it, saturated a fifth; in CODE mode the phosphor green, tinted by the
// palette. A remanence keeps 15 % of the cover's glow on cells already
// passed, so the image reads between the drops. The cover's DOM copy sits
// at the centre of its own version in characters.
//
// THE RHYTHM. Beat: births in 8 % of the columns and every visible glyph
// changes at once. Bar: uImage rises to 1 for a beat and a sweep of white
// heads crosses the screen. Phrase: CODE / IMAGE toggles, and one phrase in
// four the rain falls upward. Drop: every column lets go at once - a white
// curtain down the screen in one beat, leaving the whole cover in
// characters, held, for a bar.

const CELL_PX = { 0: 24, 1: 20, 2: 16 };
const TIER = {
  0: { curve: 0, scan: 0 },
  1: { curve: 0, scan: 0 },
  2: { curve: 0.045, scan: 0.04 }
};
const DEFAULT_TIER = 2;
const SAFE_FLOOR = 0.50;
const ATLAS_GRID = 8;

const FALL_BEATS = [2, 8];        // a drop crosses the screen in this many beats
const TRAIL_CELLS = 14;           // the trail dies over about this many cells
const BEAT_BIRTHS = 0.08;         // of the columns
const IDLE_BIRTH_S = [2.5, 6.0];  // a quiet column waits this long between drops
const RESPAWN_S = [0.3, 2.0];     // after a drop leaves
const CHANGE_RATE = [0.8, 4.0];   // glyph changes per cell per second, by bands.high
const IMAGE_BAR_PEAK = 1.0;
const IMAGE_BASE = 0.35;          // uImage at rest, so the picture is always half there
const TEXT_EVERY = 20;            // one column in twenty carries the title and the artist
const TEXT_EVERY_PHRASE = 10;     // more often on the phrases
const PHRASE_TEXT_BARS = 2;
const CODE_GREEN = [0.20, 1.00, 0.35];
const PALETTE_TINT = 0.30;
const HEAD_GAIN = 2.2;            // white: 2.2 linear lands at 250/255 after ACES, well over the bloom's threshold
const REM_GAIN = 0.15;
const HOLD_BARS = 1;              // the drop's curtain leaves the cover held this long
const DROP_CURTAIN_BEATS = 1;
const FALLBACK_BPM = 128;
const RANGE_PAD = 3;              // rows of margin in the head encoding

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function lerp(a, b, t) { return a + (b - a) * t; }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}
function srgbToLinear(c) { return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); }

// =============================================================================
// SHADERS
// =============================================================================

const quadVertexShader = `
  precision mediump float;
  varying vec2 vUv;
  varying vec2 vNdc;
  void main() {
    vUv = position.xy + 0.5;
    vNdc = position.xy * 2.0;
    gl_Position = vec4(position.xy * 2.0, 0.0, 1.0);
  }
`;

// THE UPDATE PASS, one fragment per cell. Rows are counted from the TOP,
// like the drops' heads.
const updateFragmentShader = `
  precision mediump float;
  uniform sampler2D uPrev;      // glyph/64, trail, remanence, age
  uniform sampler2D uDrops;     // per column, two drops: head (R hi, G lo), previous head (B hi, A lo)
  uniform sampler2D uMsg;       // the hidden text, glyph index / 255 per texel
  uniform vec2 uGrid;
  uniform float uDt;
  uniform float uTime;
  uniform float uDecay;         // per second
  uniform float uFlick;         // 1 on a beat's frame: every visible glyph changes
  uniform float uChange;        // random changes per cell per second
  uniform float uHold;          // 1: the trails do not die (the cover held after the curtain)
  uniform float uSweep;         // the bar's sweep, in columns; far negative when off
  uniform float uMsgLen;
  uniform float uTextEvery;
  uniform float uTextPhase;
  uniform float uSpan;          // rows + 2 * pad: the heads' encoding range
  varying vec2 vUv;
  float hash21(vec2 p) {
    p = fract(p * vec2(0.3183, 0.3679)) + vec2(0.71, 0.113);
    p += dot(p, p.yx + 19.19);
    return fract(p.x * p.y * 95.4337);
  }
  float decode(vec2 hl) { return (hl.x * 255.0 * 256.0 + hl.y * 255.0) / 65535.0 * uSpan - ${RANGE_PAD.toFixed(1)}; }
  void main() {
    vec2 cell = floor(vUv * uGrid);
    float c = cell.x;
    float rt = uGrid.y - 1.0 - cell.y;     // row from the top
    float rc = rt + 0.5;
    vec4 prev = texture2D(uPrev, vUv);
    float glyph = prev.r, trail = prev.g, rem = prev.b, age = prev.a + uDt;
    float tt = mod(uTime, 97.0);
    float hit = 0.0;
    for (int k = 0; k < 2; k++) {
      vec4 d = texture2D(uDrops, vec2((c + 0.5) / uGrid.x, (float(k) + 0.5) * 0.5));
      float head = decode(d.rg), ph = decode(d.ba);
      if (head > ph && ph < rc && rc <= head) hit = 1.0;
      if (head < ph && head <= rc && rc < ph) hit = 1.0;
    }
    if (uSweep > -5.0 && abs(c + 0.5 - uSweep) < 0.75) hit = 1.0;
    if (hit > 0.5) {
      trail = 1.0;
      glyph = floor(hash21(cell + fract(tt * 0.731) * 13.0) * 63.0) / 64.0;
      age = 0.0;
    } else {
      trail *= exp(-uDecay * uDt * (1.0 - 0.92 * uHold));
    }
    // the flicker: on the beat every visible glyph changes; otherwise a few times a second
    float rnd = hash21(cell * 1.7 + fract(tt * 0.913) * 7.0);
    if (trail > 0.04 && hit < 0.5 && (uFlick > 0.5 || rnd < uChange * uDt)) glyph = floor(hash21(cell + rnd * 31.0 + 5.0) * 63.0) / 64.0;
    // the hidden text: one column in N reads the title, then the artist, top down
    if (uMsgLen > 0.5 && mod(c + uTextPhase, uTextEvery) < 0.5) {
      float i = mod(rt + c * 3.0, uMsgLen);
      glyph = texture2D(uMsg, vec2((i + 0.5) / 64.0, 0.5)).r * (255.0 / 64.0);
    }
    rem = max(rem * exp(-uDt / 8.0), trail);
    gl_FragColor = vec4(glyph, trail, rem, min(age, 60.0));
  }
`;

function displayFragmentShader(layout) {
  return `
  precision mediump float;
  uniform sampler2D uCells;
  uniform sampler2D uAtlas;
  uniform sampler2D uCover;
  uniform vec2 uGrid;
  uniform float uCellPx;
  uniform float uImage;
  uniform float uMode;          // 0 code, 1 image
  uniform float uHasCover;
  uniform vec3 uCode;           // the phosphor, linear
  uniform vec2 uCoverScale;
  uniform float uSafeAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vUv;
  varying vec2 vNdc;
  float safeAt(vec2 ndc) {
    vec2 s = vec2(ndc.x * uSafeAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    float att = mix(0.32, 1.0, smoothstep(0.0, uSafeFeather, d));
    return ${SAFE_FLOOR.toFixed(2)} + (att - 0.32) * ${((1 - SAFE_FLOOR) / 0.68).toFixed(5)};
  }
  void main() {
    vec2 uv = vUv;
    ${layout.curve > 0 ? `
    // the tube: a barrel over the frame, black beyond the glass
    {
      vec2 p = uv * 2.0 - 1.0;
      p *= 1.0 + ${layout.curve.toFixed(3)} * dot(p, p);
      uv = p * 0.5 + 0.5;
      if (uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0) { gl_FragColor = vec4(0.0, 0.0, 0.0, 1.0); return; }
    }` : ''}
    vec2 cellF = uv * uGrid;
    vec2 cell = floor(cellF);
    vec2 local = cellF - cell;
    vec4 st = texture2D(uCells, (cell + 0.5) / uGrid);
    float gi = floor(st.r * 64.0 + 0.5);
    vec2 g = vec2(mod(gi, ${ATLAS_GRID.toFixed(1)}), floor(gi / ${ATLAS_GRID.toFixed(1)}));
    // the atlas is flipped on upload: row 0 of the PNG is v = 1
    vec2 auv = vec2((g.x + local.x) / ${ATLAS_GRID.toFixed(1)}, (${ATLAS_GRID.toFixed(1)} - 1.0 - g.y + local.y) / ${ATLAS_GRID.toFixed(1)});
    float sd = texture2D(uAtlas, auv).r;
    float w = 1.0 / uCellPx;
    float ink = smoothstep(0.5 - w, 0.5 + w, sd);
    // the cover under the cell's centre
    vec2 cuv = ((cell + 0.5) / uGrid - 0.5) * uCoverScale + 0.5;
    vec3 cv = texture2D(uCover, clamp(cuv, 0.0, 1.0)).rgb * uHasCover;
    float L = dot(cv, vec3(0.2126, 0.7152, 0.0722));
    float img = uImage * uHasCover;
    float gate = mix(1.0, L, img);
    float trail = st.g;
    float head = smoothstep(0.93, 1.0, trail);
    vec3 sat = mix(vec3(L), cv, 1.2);
    vec3 base = mix(uCode, sat, uMode * uHasCover);
    vec3 col = base * (trail * gate);
    col = mix(col, vec3(${HEAD_GAIN.toFixed(2)}) * gate, head);
    col += cv * (st.b * ${REM_GAIN.toFixed(2)} * L);
    col *= ink;
    ${layout.scan > 0 ? `col *= 1.0 - ${layout.scan.toFixed(3)} * (0.5 + 0.5 * sin(gl_FragCoord.y * 3.14159));` : ''}
    col *= safeAt(vNdc);
    gl_FragColor = vec4(col, 1.0);
  }
`;
}

// =============================================================================

// Materials cached at module level with their uniform holders (the Pi's
// compile cost, three.js freeing programs with their last material).
const MATERIALS = new Map();
function materialsFor(tier) {
  const key = String(tier);
  let m = MATERIALS.get(key);
  if (m) return m;
  const layout = TIER[tier];
  const updateUniforms = {
    uPrev: { value: null }, uDrops: { value: null }, uMsg: { value: null },
    uGrid: { value: new THREE.Vector2(1, 1) }, uDt: { value: 0 }, uTime: { value: 0 }, uDecay: { value: 1 },
    uFlick: { value: 0 }, uChange: { value: 1 }, uHold: { value: 0 }, uSweep: { value: -10 },
    uMsgLen: { value: 0 }, uTextEvery: { value: TEXT_EVERY }, uTextPhase: { value: 0 }, uSpan: { value: 10 }
  };
  const update = new THREE.ShaderMaterial({ uniforms: updateUniforms, vertexShader: quadVertexShader, fragmentShader: updateFragmentShader, depthTest: false, depthWrite: false });
  const displayUniforms = {
    uCells: { value: null }, uAtlas: { value: null }, uCover: { value: null },
    uGrid: { value: new THREE.Vector2(1, 1) }, uCellPx: { value: 16 }, uImage: { value: IMAGE_BASE }, uMode: { value: 0 }, uHasCover: { value: 0 },
    uCode: { value: new THREE.Vector3(CODE_GREEN[0], CODE_GREEN[1], CODE_GREEN[2]) }, uCoverScale: { value: new THREE.Vector2(1, 1) },
    uSafeAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
  };
  const display = new THREE.ShaderMaterial({ uniforms: displayUniforms, vertexShader: quadVertexShader, fragmentShader: displayFragmentShader(layout), depthTest: false, depthWrite: false });
  m = { updateUniforms, update, displayUniforms, display };
  MATERIALS.set(key, m);
  return m;
}

export class MatrixVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER[tier] ? tier : DEFAULT_TIER;
    this._layout = TIER[this._tier];
    this._cellPx = CELL_PX[this._tier];
    const fi = parseFloat(urlParam('matriximage'));
    this._forcedImage = Number.isFinite(fi) ? clamp01(fi) : null;
    const fm = urlParam('matrixmode');
    this._forcedMode = fm === 'code' ? 0 : (fm === 'image' ? 1 : null);
    this._forceDrop = urlParam('matrixdrop') === '1';
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    this._time = 0;
    this._paused = false;
    this._cols = 0; this._rows = 0;
    this._mats = materialsFor(this._tier);
    this._uu = this._mats.updateUniforms;
    this._du = this._mats.displayUniforms;
    this._resetUniforms();
    this._atlas = getMatrixAtlas();
    if (this._atlas) this._du.uAtlas.value = this._atlas.texture;
    this._mode = 0;
    this._image = IMAGE_BASE;
    this._imageBar = 0;
    this._hold = 0;
    this._holdUntil = -1e9;
    this._sweep = -10; this._sweepUntil = -1e9;
    this._flick = false;
    this._dir = 1;
    this._lastBeatPhase = null; this._lastBarPhase = null; this._lastPhrase = null;
    this._ownBeat = 0; this._beatsSeen = 0; this._barsSeen = 0; this._barsSincePhrase = 0; this._phraseCount = 0;
    this._lastDropAt = -1e9;
    this._lowSmooth = 0.5; this._highSmooth = 0.5;
    this._lastPaletteRef = undefined;
    this._lastArtworkUrl = undefined;
    this._lastText = '';
    this._artworkTexture = null;
    this._drops = null;
    this._dropsTex = null;
    this._msgTex = new THREE.DataTexture(new Uint8Array(64), 64, 1, THREE.RedFormat, THREE.UnsignedByteType);
    this._msgTex.minFilter = this._msgTex.magFilter = THREE.NearestFilter;
    this._msgTex.needsUpdate = true;
    this._uu.uMsg.value = this._msgTex;
    this._rt = [null, null];
    this._which = 0;
    this._pendingDt = 0;

    this._ortho = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0, 10);
    this._ortho.position.z = 1;
    this._updateScene = new THREE.Scene();
    this._updateQuad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this._mats.update);
    this._updateQuad.frustumCulled = false;
    this._updateScene.add(this._updateQuad);

    this._quad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this._mats.display);
    this._quad.frustumCulled = false;
    this._quad.renderOrder = -1000;
    const self = this;
    // THE UPDATE PASS RUNS HERE, just before the display quad draws, with
    // the drawing buffer's real size in hand.
    this._quad.onBeforeRender = function (renderer) {
      renderer.getDrawingBufferSize(_size);
      self._ensureGrid(renderer, _size.x, _size.y);
      if (self._pendingDt > 0 && self._drops) {
        self._uu.uDt.value = self._pendingDt;
        self._pendingDt = 0;
        const prev = renderer.getRenderTarget();
        self._uu.uPrev.value = self._rt[self._which].texture;
        renderer.setRenderTarget(self._rt[1 - self._which]);
        renderer.render(self._updateScene, self._ortho);
        renderer.setRenderTarget(prev);
        self._which = 1 - self._which;
        self._uu.uFlick.value = 0;
      }
      self._du.uCells.value = self._rt[self._which].texture;
    };
    this._scene.add(this._quad);
  }

  _resetUniforms() {
    const u = this._uu, d = this._du;
    u.uDt.value = 0; u.uTime.value = 0; u.uFlick.value = 0; u.uHold.value = 0; u.uSweep.value = -10; u.uMsgLen.value = 0; u.uTextEvery.value = TEXT_EVERY; u.uTextPhase.value = 0;
    d.uImage.value = IMAGE_BASE; d.uMode.value = 0; d.uHasCover.value = 0; d.uCover.value = null; d.uCellPx.value = this._cellPx;
    d.uCode.value.set(CODE_GREEN[0], CODE_GREEN[1], CODE_GREEN[2]);
  }

  // The grid follows the drawing buffer: a change of size rebuilds the cell
  // targets from black and the drops from scratch.
  _ensureGrid(renderer, wpx, hpx) {
    const cols = Math.max(4, Math.ceil(wpx / this._cellPx)), rows = Math.max(4, Math.ceil(hpx / this._cellPx));
    if (cols === this._cols && rows === this._rows && this._rt[0]) return;
    this._cols = cols; this._rows = rows;
    for (const t of this._rt) if (t) t.dispose();
    const type = FLOAT_TEXTURES_OK ? THREE.FloatType : THREE.UnsignedByteType;
    const opts = { type: type, format: THREE.RGBAFormat, minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter, depthBuffer: false, stencilBuffer: false, generateMipmaps: false };
    this._rt = [new THREE.WebGLRenderTarget(cols, rows, opts), new THREE.WebGLRenderTarget(cols, rows, opts)];
    const prev = renderer.getRenderTarget();
    renderer.setClearColor(0x000000, 1);
    for (const t of this._rt) { renderer.setRenderTarget(t); renderer.clear(true, false, false); }
    renderer.setRenderTarget(prev);
    this._which = 0;
    this._uu.uGrid.value.set(cols, rows);
    this._du.uGrid.value.set(cols, rows);
    this._uu.uSpan.value = rows + 2 * RANGE_PAD;
    // the drops: two per column
    if (this._dropsTex) this._dropsTex.dispose();
    this._dropsData = new Uint8Array(cols * 2 * 4);
    this._dropsTex = new THREE.DataTexture(this._dropsData, cols, 2, THREE.RGBAFormat, THREE.UnsignedByteType);
    this._dropsTex.minFilter = this._dropsTex.magFilter = THREE.NearestFilter;
    this._uu.uDrops.value = this._dropsTex;
    this._drops = [];
    for (let c = 0; c < cols; c++) for (let k = 0; k < 2; k++) this._drops.push({ active: false, head: -1, prev: -1, speed: 0, dir: 1, wait: lerp(0, IDLE_BIRTH_S[1], Math.random()) * (k === 0 ? 0.6 : 1.4) });
    this._writeDrops();
    this._fitCover();
  }

  _writeDrops() {
    const d = this._dropsData, span = this._rows + 2 * RANGE_PAD;
    for (let i = 0; i < this._drops.length; i++) {
      const dr = this._drops[i];
      const c = i >> 1, k = i & 1;
      const o = (k * this._cols + c) * 4;
      const h = dr.active ? dr.head : -1, p = dr.active ? dr.prev : -1;
      const hv = Math.round(clamp01((h + RANGE_PAD) / span) * 65535), pv = Math.round(clamp01((p + RANGE_PAD) / span) * 65535);
      d[o] = hv >> 8; d[o + 1] = hv & 255; d[o + 2] = pv >> 8; d[o + 3] = pv & 255;
    }
    this._dropsTex.needsUpdate = true;
  }

  _fitCover() {
    const tex = this._artworkTexture;
    if (!tex || !tex.image || !this._cols) return;
    const frameAspect = this._cols / this._rows;
    const texAspect = tex.image.width / tex.image.height;
    this._du.uCoverScale.value.copy(coverScale(frameAspect, texAspect));
  }

  _birth(dr, speedCells, dir, fromEdge) {
    dr.active = true;
    dr.dir = dir;
    dr.speed = speedCells;
    dr.head = dir > 0 ? (fromEdge ? -0.5 : -0.5) : this._rows + 0.5;
    dr.prev = dr.head - dir * 0.01;
  }

  _loadArtwork(url) {
    const self = this;
    loadArtworkTexture(url, { wrap: THREE.ClampToEdgeWrapping, mipmaps: true }, function (texture) {
      if (!self._scene) { texture.dispose(); return; }
      if (self._artworkTexture) self._artworkTexture.dispose();
      self._artworkTexture = texture;
      self._du.uCover.value = texture;
      self._du.uHasCover.value = 1;
      self._fitCover();
    }, function () { /* code mode, then */ });
  }

  _setMessage(title, artist) {
    const atlas = this._atlas;
    const text = ((title || '') + '   ' + (artist || '')).toUpperCase();
    if (text === this._lastText) return;
    this._lastText = text;
    const data = this._msgTex.image.data;
    data.fill(atlas ? atlas.meta.blank : 63);
    let n = 0;
    if (atlas) {
      const m = atlas.meta;
      for (const ch of text.normalize('NFD').replace(/[̀-ͯ]/g, '')) {
        if (n >= 64) break;
        if (ch >= 'A' && ch <= 'Z') data[n++] = m.latin + (ch.charCodeAt(0) - 65);
        else if (ch >= '0' && ch <= '9') data[n++] = m.digits + (ch.charCodeAt(0) - 48);
        else if (ch === ' ') data[n++] = m.blank;
      }
    }
    this._msgTex.needsUpdate = true;
    this._uu.uMsgLen.value = n > 2 ? n : 0;
  }

  onTrackChange(profile) {
    this._lastText = '';
  }

  update(deltaTime, profile) {
    this._paused = profile.playbackState === 'PAUSED';
    if (this._paused) return;
    const dt = Math.min(deltaTime, 0.05);
    this._time += dt;
    const director = profile.director || {};
    const bands = director.bands || null;
    const has = !!(bands && director.hasReliableGrid);
    const kb = 1 - Math.exp(-dt / 0.25);
    this._lowSmooth += ((has ? clamp01(bands.low) : 0.5 + 0.3 * Math.sin(this._time * 1.7)) - this._lowSmooth) * kb;
    this._highSmooth += ((has ? clamp01(bands.high) : 0.5 + 0.3 * Math.sin(this._time * 2.3 + 1)) - this._highSmooth) * kb;
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || FALLBACK_BPM;
    const beatS = 60 / Math.max(60, bpm);

    // --- the cover, the palette, the text ---------------------------------------
    if (profile.artworkUrl !== this._lastArtworkUrl) {
      this._lastArtworkUrl = profile.artworkUrl;
      if (profile.artworkUrl) this._loadArtwork(profile.artworkUrl);
      else { this._du.uHasCover.value = 0; this._du.uCover.value = null; }
    }
    if (profile.artworkPalette !== this._lastPaletteRef) {
      this._lastPaletteRef = profile.artworkPalette;
      const pal = profile.artworkPalette;
      const dom = pal && pal.dominantColors && pal.dominantColors.length ? pal.dominantColors[0] : null;
      const c = this._du.uCode.value.set(CODE_GREEN[0], CODE_GREEN[1], CODE_GREEN[2]);
      if (dom != null) {
        const col = new THREE.Color(dom); col.getHSL(_hsl); col.setHSL(_hsl.h, Math.max(0.5, _hsl.s), 0.55);
        c.lerp(new THREE.Vector3(srgbToLinear(col.r), srgbToLinear(col.g), srgbToLinear(col.b)), PALETTE_TINT);
      }
    }
    this._setMessage(profile.title, profile.artist);

    // --- the grid, or a beat of our own ------------------------------------------
    let newBeat = false, newBar = false, newPhrase = false;
    if (profile.beatPhase != null) {
      if (this._lastBeatPhase != null && profile.beatPhase < this._lastBeatPhase - 0.5) newBeat = true;
      if (this._lastBeatPhase == null) newBeat = true;
      this._lastBeatPhase = profile.beatPhase;
      if (profile.barPhase != null) {
        if (this._lastBarPhase != null && profile.barPhase < this._lastBarPhase - 0.5) newBar = true;
        this._lastBarPhase = profile.barPhase;
      } else if (newBeat && (this._beatsSeen % 4) === 0) newBar = true;
      if (profile.phraseIndex != null && this._lastPhrase != null && profile.phraseIndex !== this._lastPhrase) newPhrase = true;
      if (profile.phraseIndex != null) this._lastPhrase = profile.phraseIndex;
    } else {
      this._ownBeat += dt;
      if (this._ownBeat >= beatS) { this._ownBeat -= beatS; newBeat = true; }
      if (newBeat && (this._beatsSeen % 4) === 0) newBar = true;
    }
    if (newBeat) this._beatsSeen++;
    if (newBar) { this._barsSeen++; this._barsSincePhrase++; if (!newPhrase && this._barsSincePhrase >= 4) newPhrase = true; }
    if (newPhrase) this._barsSincePhrase = 0;

    const ev = director.nextEvent;
    const dropNow = (ev && ev.type === 'drop' && ev.msUntil != null && ev.msUntil <= 0 && this._time - this._lastDropAt > 4.0)
      || (this._forceDrop && this._time - this._lastDropAt > 8.0);

    // --- the events ----------------------------------------------------------------
    if (newPhrase) {
      this._phraseCount++;
      this._mode = this._mode ? 0 : 1;
      this._dir = (this._phraseCount % 4 === 3) ? -1 : 1;
    }
    if (newBar) {
      this._imageBar = 1;
      this._sweep = this._dir > 0 ? -1 : this._cols + 1;
      this._sweepUntil = this._time + beatS;
      this._sweepFrom = this._time;
    }
    if (newBeat) this._flick = true;
    if (dropNow) {
      this._lastDropAt = this._time;
      this._holdUntil = this._time + DROP_CURTAIN_BEATS * beatS + HOLD_BARS * 4 * beatS;
      this._curtainUntil = this._time + DROP_CURTAIN_BEATS * beatS;
    }

    // --- the drops, on the CPU ---------------------------------------------------------
    if (this._drops) {
      const rows = this._rows, cols = this._cols;
      const speedScale = 0.8 + 0.4 * this._lowSmooth;
      const births = newBeat ? Math.max(1, Math.round(cols * BEAT_BIRTHS)) : 0;
      let born = 0;
      if (dropNow) {
        // the curtain: every column, one beat for the screen, the first slot
        for (let c = 0; c < cols; c++) this._birth(this._drops[c * 2], rows / (DROP_CURTAIN_BEATS * beatS), this._dir, true);
      }
      for (let i = 0; i < this._drops.length; i++) {
        const dr = this._drops[i];
        if (dr.active) {
          dr.prev = dr.head;
          dr.head += dr.dir * dr.speed * speedScale * dt;
          if ((dr.dir > 0 && dr.prev > rows + 1) || (dr.dir < 0 && dr.prev < -1)) { dr.active = false; dr.wait = lerp(RESPAWN_S[0], RESPAWN_S[1], Math.random()); }
        } else {
          dr.wait -= dt;
          const beatBirth = births > 0 && born < births && Math.random() < births / cols * 3.0;
          if (dr.wait <= 0 || beatBirth) {
            if (beatBirth) born++;
            const beats = lerp(FALL_BEATS[0], FALL_BEATS[1], Math.random());
            this._birth(dr, rows / (beats * beatS), this._dir, true);
            if (!beatBirth) dr.wait = lerp(IDLE_BIRTH_S[0], IDLE_BIRTH_S[1], Math.random());
          }
        }
      }
      this._writeDrops();
      // the trail dies over TRAIL_CELLS behind a typical head
      const typical = rows / (lerp(FALL_BEATS[0], FALL_BEATS[1], 0.5) * beatS) * speedScale;
      this._uu.uDecay.value = typical / TRAIL_CELLS * 2.3;
    }

    // --- uImage, the hold, the sweep ---------------------------------------------------------
    this._imageBar = Math.max(0, this._imageBar - dt / beatS);
    this._hold = this._time < this._holdUntil ? 1 : 0;
    const image = this._forcedImage != null ? this._forcedImage : Math.max(IMAGE_BASE, this._hold ? 1 : this._imageBar * IMAGE_BAR_PEAK);
    const mode = this._forcedMode != null ? this._forcedMode : this._mode;
    if (this._time < this._sweepUntil) {
      const k = (this._time - this._sweepFrom) / Math.max(1e-3, this._sweepUntil - this._sweepFrom);
      this._uu.uSweep.value = this._dir > 0 ? -1 + (this._cols + 2) * k : this._cols + 1 - (this._cols + 2) * k;
    } else this._uu.uSweep.value = -10;

    const u = this._uu, d = this._du;
    u.uTime.value = this._time;
    u.uFlick.value = this._flick ? 1 : 0; this._flick = false;
    u.uChange.value = lerp(CHANGE_RATE[0], CHANGE_RATE[1], this._highSmooth);
    u.uHold.value = this._hold;
    u.uTextEvery.value = this._barsSincePhrase < PHRASE_TEXT_BARS ? TEXT_EVERY_PHRASE : TEXT_EVERY;
    u.uTextPhase.value = this._phraseCount % 7;
    d.uImage.value = image;
    d.uMode.value = mode;
    this._pendingDt = dt;
  }

  get probe() {
    return {
      time: this._time, cols: this._cols, rows: this._rows, mode: this._du.uMode.value, image: this._du.uImage.value, hold: this._hold, dir: this._dir,
      active: this._drops ? this._drops.filter((d) => d.active).length : 0, hasCover: this._du.uHasCover.value, atlas: !!this._atlas, msgLen: this._uu.uMsgLen.value,
      beatsSeen: this._beatsSeen, barsSeen: this._barsSeen, phrases: this._phraseCount, sweep: this._uu.uSweep.value, tier: this._tier, float: FLOAT_TEXTURES_OK
    };
  }

  dispose() {
    this._scene.remove(this._quad);
    this._quad.geometry.dispose();
    this._updateQuad.geometry.dispose();
    for (const t of this._rt) if (t) t.dispose();
    if (this._dropsTex) this._dropsTex.dispose();
    this._msgTex.dispose();
    if (this._artworkTexture) { this._artworkTexture.dispose(); this._artworkTexture = null; }
    this._du.uCover.value = null; this._du.uCells.value = null; this._uu.uPrev.value = null; this._uu.uDrops.value = null; this._uu.uMsg.value = null;
    this._scene = null;
  }
}

const _size = new THREE.Vector2();
const _hsl = { h: 0, s: 0, l: 0 };
